import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Tabs, Tab, Button, Grid, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Divider,
  FormControlLabel, Checkbox, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack, Edit, Print, Add, Delete, CheckCircle, RadioButtonUnchecked,
  Assignment, MedicalServices, LocalHospital, PostAdd,
  UploadFile, CloudDownload, InsertDriveFile,
} from '@mui/icons-material';
import {
  surgeryService, consentFormService, consentFormDocumentService,
  preOpChecklistService, otRoomService, userService, patientService,
} from '../../services/api';
import TopBar from '../../components/TopBar';

const toLocalDateTimeInput = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ─── Consent templates ────────────────────────────────────────────────────────
const CONSENT_TEMPLATES = {
  Surgery: `I, the undersigned patient (or legal guardian), hereby give my free and informed consent to undergo the surgical procedure as recommended by my treating physician at Vedanta Hospital.\n\nI acknowledge that I have been explained:\n1. The nature, purpose and details of the proposed surgical procedure.\n2. The risks and possible complications that may arise during or after the procedure.\n3. The expected benefits and possible alternatives to the procedure.\n4. The consequences of not undergoing the procedure.\n\nI understand that no guarantee of specific results has been given. I authorize the surgical team to perform additional procedures they deem necessary for my safety during the operation.\n\nI consent to the administration of blood transfusions or other medical treatments that may be required during the procedure.`,

  Anesthesia: `I, the undersigned, hereby consent to the administration of anesthesia (general, regional, or local as deemed appropriate by the anesthesiologist) in connection with my planned surgical procedure at Vedanta Hospital.\n\nI understand that anesthesia carries certain risks including but not limited to:\n1. Allergic or adverse reactions to anesthetic agents.\n2. Temporary confusion, nausea, or vomiting upon recovery.\n3. Respiratory complications in rare cases.\n4. In very rare cases, more serious adverse events.\n\nI confirm that I have disclosed all relevant medical history, allergies, current medications, and prior anesthesia experiences to my care team.`,

  BloodTransfusion: `I, the undersigned, hereby give my informed consent to receive blood and/or blood products (including packed red cells, platelets, fresh frozen plasma or other derivatives) as deemed medically necessary by my attending physician during my treatment at Vedanta Hospital.\n\nI understand the following regarding blood transfusion:\n1. The medical reasons why a transfusion may be necessary.\n2. The potential risks including transfusion reactions, transmission of infectious agents (though rigorously screened), and other complications.\n3. The alternatives that may be available.\n\nI acknowledge that blood products are carefully screened and tested but that no procedure is entirely without risk.`,
};

const STATUS_COLORS = {
  Scheduled:  { bg: '#e3f2fd', color: '#1565c0' },
  InProgress: { bg: '#fff3e0', color: '#e65100' },
  Completed:  { bg: '#e8f5e9', color: '#2e7d32' },
  Cancelled:  { bg: '#ffebee', color: '#c62828' },
  Postponed:  { bg: '#f3e5f5', color: '#6a1b9a' },
};


function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const SurgeryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'Admin';
  const isDoctor = user.role === 'Doctor';
  const canEdit = isAdmin || isDoctor;

  const [tab, setTab] = useState(0);
  const [surgery, setSurgery] = useState(null);
  const [loading, setLoading] = useState(true);

  // consent state
  const [consents, setConsents] = useState([]);
  const [consentDialog, setConsentDialog] = useState({ open: false, editing: null });
  const [consentForm, setConsentForm] = useState({ consentType: 'Surgery', consentText: '', status: 'Pending', guardianName: '', guardianRelation: '', witnessName: '' });

  // signed copy upload state
  const [docsDialog, setDocsDialog] = useState({ open: false, consentId: null, consentType: '' });
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const docsFileInputRef = useRef(null);

  // pre-op state
  const [checklist, setChecklist] = useState(null);
  const [checklistDirty, setChecklistDirty] = useState(false);
  const [npoSince, setNpoSince] = useState('');
  const [checklistNotes, setChecklistNotes] = useState('');
  const [savingChecklist, setSavingChecklist] = useState(false);

  // post-op state
  const [postOpForm, setPostOpForm] = useState({ postOpNotes: '', complications: '', dischargeInstructions: '' });
  const [savingPostOp, setSavingPostOp] = useState(false);
  const [postOpSaved, setPostOpSaved] = useState(false);

  const loadSurgery = useCallback(async () => {
    setLoading(true);
    try {
      const res = await surgeryService.getById(id);
      setSurgery(res.data);
      setPostOpForm({
        postOpNotes: res.data.postOpNotes || '',
        complications: res.data.complications || '',
        dischargeInstructions: res.data.dischargeInstructions || '',
      });
    } catch {
      alert('Failed to load surgery');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadConsents = useCallback(async () => {
    try {
      const res = await consentFormService.getAll(id);
      setConsents(res.data);
    } catch {}
  }, [id]);

  const loadChecklist = useCallback(async () => {
    try {
      const res = await preOpChecklistService.get(id);
      // API always returns an object (never null) after migration
      const data = res.data || { npoSince: null, notes: null, items: [] };
      setChecklist(data);
      setNpoSince(data.npoSince ? toLocalDateTimeInput(data.npoSince) : '');
      setChecklistNotes(data.notes || '');
    } catch {}
  }, [id]);

  useEffect(() => {
    loadSurgery();
    loadConsents();
    loadChecklist();
  }, [loadSurgery, loadConsents, loadChecklist]);

  // ── Status change ──
  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Mark surgery as ${newStatus}?`)) return;
    try {
      await surgeryService.updateStatus(id, newStatus);
      loadSurgery();
    } catch { alert('Failed to update status'); }
  };

  // ── Consent helpers ──
  const openConsentDialog = (existing = null) => {
    if (existing) {
      setConsentForm({
        consentType: existing.consentType,
        consentText: existing.consentText || '',
        status: existing.status,
        guardianName: existing.guardianName || '',
        guardianRelation: existing.guardianRelation || '',
        witnessName: existing.witnessName || '',
      });
      setConsentDialog({ open: true, editing: existing });
    } else {
      setConsentForm({ consentType: 'Surgery', consentText: CONSENT_TEMPLATES.Surgery, status: 'Pending', guardianName: '', guardianRelation: '', witnessName: '' });
      setConsentDialog({ open: true, editing: null });
    }
  };

  const handleConsentTypeChange = (type) => {
    setConsentForm(prev => ({
      ...prev,
      consentType: type,
      consentText: !prev.consentText || Object.values(CONSENT_TEMPLATES).includes(prev.consentText)
        ? CONSENT_TEMPLATES[type] || ''
        : prev.consentText,
    }));
  };

  const handleConsentSubmit = async () => {
    try {
      if (consentDialog.editing) {
        await consentFormService.update(id, consentDialog.editing.id, consentForm);
      } else {
        await consentFormService.create(id, consentForm);
      }
      setConsentDialog({ open: false, editing: null });
      loadConsents();
    } catch { alert('Failed to save consent form'); }
  };

  const handleConsentDelete = async (consentId) => {
    if (!window.confirm('Delete this consent form?')) return;
    try {
      await consentFormService.delete(id, consentId);
      loadConsents();
    } catch { alert('Failed to delete consent form'); }
  };

  const handleMarkSigned = async (consent) => {
    try {
      await consentFormService.update(id, consent.id, { ...consent, status: 'Signed' });
      loadConsents();
    } catch { alert('Failed to update consent status'); }
  };

  // ── Signed copy document helpers ──
  const openDocsDialog = async (consent) => {
    setDocsDialog({ open: true, consentId: consent.id, consentType: consent.consentType });
    setDocsLoading(true);
    try {
      const res = await consentFormDocumentService.getAll(id, consent.id);
      setDocs(res.data);
    } catch { alert('Failed to load documents'); }
    finally { setDocsLoading(false); }
  };

  const handleDocUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await consentFormDocumentService.upload(id, docsDialog.consentId, file);
      const res = await consentFormDocumentService.getAll(id, docsDialog.consentId);
      setDocs(res.data);
      loadConsents(); // refresh doc count in the table
    } catch (err) {
      alert(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (docsFileInputRef.current) docsFileInputRef.current.value = '';
    }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm('Delete this uploaded document?')) return;
    try {
      await consentFormDocumentService.delete(id, docsDialog.consentId, docId);
      setDocs(prev => prev.filter(d => d.id !== docId));
      loadConsents();
    } catch { alert('Failed to delete document'); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ── Checklist helpers ──
  const toggleChecklistItem = (itemId) => {
    setChecklist(prev => ({
      ...prev,
      items: (prev.items || []).map(item =>
        item.itemId === itemId ? { ...item, isCompleted: !item.isCompleted } : item
      ),
    }));
    setChecklistDirty(true);
  };

  const saveChecklist = async () => {
    setSavingChecklist(true);
    try {
      await preOpChecklistService.upsert(id, {
        npoSince: npoSince || null,
        notes: checklistNotes || null,
        entries: (checklist?.items || []).map(item => ({ itemId: item.itemId, isCompleted: item.isCompleted })),
      });
      setChecklistDirty(false);
      loadChecklist();
    } catch { alert('Failed to save checklist'); }
    finally { setSavingChecklist(false); }
  };

  // ── Post-op helpers ──
  const savePostOp = async () => {
    setSavingPostOp(true);
    setPostOpSaved(false);
    try {
      await surgeryService.update(id, {
        patientId: surgery.patient.id,
        surgeonId: surgery.surgeon.id,
        otRoomId: surgery.otRoom?.id || null,
        surgeryName: surgery.surgeryName,
        scheduledDateTime: surgery.scheduledDateTime,
        estimatedDurationMinutes: surgery.estimatedDurationMinutes,
        actualStartTime: surgery.actualStartTime,
        actualEndTime: surgery.actualEndTime,
        status: surgery.status,
        notes: surgery.notes,
        postOpNotes: postOpForm.postOpNotes || null,
        complications: postOpForm.complications || null,
        dischargeInstructions: postOpForm.dischargeInstructions || null,
        teamMembers: (surgery.teamMembers || []).map(tm => ({ userId: tm.userId, role: tm.role })),
      });
      setPostOpSaved(true);
      loadSurgery();
    } catch { alert('Failed to save post-op notes'); }
    finally { setSavingPostOp(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Surgery Detail" onBack={() => navigate('/surgery-planner')} />
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
    </div>
  );

  if (!surgery) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Surgery Detail" onBack={() => navigate('/surgery-planner')} />
      <Container sx={{ py: 4 }}><Typography>Surgery not found.</Typography></Container>
    </div>
  );

  const sc = STATUS_COLORS[surgery.status] || {};
  const checklistItems = checklist?.items || [];
  const completedItems = checklistItems.filter(i => i.isCompleted).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Surgery Detail" onBack={() => navigate('/surgery-planner')} />
      <Container maxWidth="lg" sx={{ py: 4 }}>

        {/* Header card */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>{surgery.surgeryName}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {surgery.patient ? `${surgery.patient.firstName} ${surgery.patient.lastName}` : '—'} &bull;{' '}
                {new Date(surgery.scheduledDateTime).toLocaleString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Box sx={{ px: 1.5, py: 0.3, borderRadius: 1, bgcolor: sc.bg, color: sc.color, fontSize: '0.78rem', fontWeight: 700 }}>
                  {surgery.status}
                </Box>
                {surgery.otRoom && (
                  <Chip size="small" label={surgery.otRoom.name} variant="outlined" />
                )}
                {surgery.surgeon && (
                  <Chip size="small" label={`Dr. ${surgery.surgeon.firstName} ${surgery.surgeon.lastName}`} variant="outlined" />
                )}
              </Box>
            </Box>
            {/* Status actions */}
            {canEdit && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {surgery.status === 'Scheduled' && (
                  <Button variant="outlined" color="warning" size="small" onClick={() => handleStatusChange('InProgress')}>Mark In Progress</Button>
                )}
                {surgery.status === 'InProgress' && (
                  <Button variant="contained" color="success" size="small" onClick={() => handleStatusChange('Completed')}>Mark Completed</Button>
                )}
                {['Scheduled', 'InProgress'].includes(surgery.status) && (
                  <Button variant="outlined" color="error" size="small" onClick={() => handleStatusChange('Cancelled')}>Cancel Surgery</Button>
                )}
                {surgery.status === 'Scheduled' && (
                  <Button variant="outlined" color="secondary" size="small" onClick={() => handleStatusChange('Postponed')}>Postpone</Button>
                )}
                <Button variant="outlined" size="small" startIcon={<Edit />}
                  onClick={() => navigate('/surgery-planner', { state: { editId: surgery.id } })}>
                  Edit
                </Button>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tab icon={<LocalHospital />} iconPosition="start" label="Overview" />
            <Tab icon={<Assignment />} iconPosition="start" label={`Consent Forms${consents.length > 0 ? ` (${consents.length})` : ''}`} />
            <Tab icon={<MedicalServices />} iconPosition="start" label={`Pre-op Checklist${checklist ? ` (${completedItems}/${checklistItems.length})` : ''}`} />
            <Tab icon={<PostAdd />} iconPosition="start" label="Post-op Notes" />
          </Tabs>

          <Box sx={{ p: 3 }}>

            {/* ── Tab 0: Overview ── */}
            <TabPanel value={tab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Patient</Typography>
                  {surgery.patient && (
                    <Box>
                      <Typography fontWeight={600}>{surgery.patient.firstName} {surgery.patient.lastName}</Typography>
                      <Typography variant="body2" color="text.secondary">{surgery.patient.gender} &bull; {surgery.patient.phoneNumber}</Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Primary Surgeon</Typography>
                  {surgery.surgeon && (
                    <Typography fontWeight={600}>Dr. {surgery.surgeon.firstName} {surgery.surgeon.lastName}{surgery.surgeon.specialization ? ` — ${surgery.surgeon.specialization}` : ''}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>OT Room</Typography>
                  <Typography>{surgery.otRoom?.name || '—'}</Typography>
                </Grid>
                {surgery.surgeryType && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Surgery Type</Typography>
                    <Chip size="small" label={surgery.surgeryType.name} color="info" variant="outlined" />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Estimated Duration</Typography>
                  <Typography>{surgery.estimatedDurationMinutes} minutes</Typography>
                </Grid>
                {surgery.actualStartTime && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Actual Start</Typography>
                    <Typography>{new Date(surgery.actualStartTime).toLocaleString('en-IN')}</Typography>
                  </Grid>
                )}
                {surgery.actualEndTime && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Actual End</Typography>
                    <Typography>{new Date(surgery.actualEndTime).toLocaleString('en-IN')}</Typography>
                  </Grid>
                )}
                {surgery.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Pre-op Notes</Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{surgery.notes}</Typography>
                  </Grid>
                )}
                {surgery.teamMembers?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Surgical Team</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Role</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {surgery.teamMembers.map((tm, i) => (
                          <TableRow key={i}>
                            <TableCell>{tm.user ? `${tm.user.firstName} ${tm.user.lastName}` : '—'}</TableCell>
                            <TableCell>{tm.role}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            {/* ── Tab 1: Consent Forms ── */}
            <TabPanel value={tab} index={1}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                {canEdit && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => openConsentDialog()}>
                    Add Consent Form
                  </Button>
                )}
              </Box>
              {consents.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No consent forms added yet. Click "Add Consent Form" to create one.
                </Typography>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Guardian</TableCell>
                      <TableCell>Witness</TableCell>
                      <TableCell>Signed At</TableCell>
                      <TableCell>Signed Copies</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consents.map(c => (
                      <TableRow key={c.id}>
                        <TableCell sx={{ fontWeight: 600 }}>{c.consentType}</TableCell>
                        <TableCell>
                          <Box sx={{
                            display: 'inline-block', px: 1.5, py: 0.3, borderRadius: 1, fontSize: '0.75rem', fontWeight: 600,
                            bgcolor: c.status === 'Signed' ? '#e8f5e9' : '#fff8e1',
                            color: c.status === 'Signed' ? '#2e7d32' : '#f57f17',
                          }}>
                            {c.status}
                          </Box>
                        </TableCell>
                        <TableCell>{c.guardianName || '—'}{c.guardianRelation ? ` (${c.guardianRelation})` : ''}</TableCell>
                        <TableCell>{c.witnessName || '—'}</TableCell>
                        <TableCell>{c.signedAt ? new Date(c.signedAt).toLocaleDateString('en-IN') : '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={<InsertDriveFile sx={{ fontSize: '14px !important' }} />}
                            label={c.documentCount > 0 ? `${c.documentCount} file${c.documentCount > 1 ? 's' : ''}` : 'None'}
                            color={c.documentCount > 0 ? 'primary' : 'default'}
                            variant="outlined"
                            onClick={() => openDocsDialog(c)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {canEdit && c.status === 'Pending' && (
                              <Tooltip title="Mark Signed">
                                <IconButton size="small" color="success" onClick={() => handleMarkSigned(c)}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Upload Signed Copy">
                              <IconButton size="small" color="secondary" onClick={() => openDocsDialog(c)}>
                                <UploadFile fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print">
                              <IconButton size="small" color="primary"
                                onClick={() => navigate(`/surgery-planner/${id}/consent/${c.id}/print`)}>
                                <Print fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openConsentDialog(c)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => handleConsentDelete(c.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabPanel>

            {/* ── Tab 2: Pre-op Checklist ── */}
            <TabPanel value={tab} index={2}>
              {checklist && checklistItems.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No pre-op checklist items configured for this surgery type. Configure them in Surgery Type Mapping.
                </Typography>
              )}
              {checklist && checklistItems.length > 0 && (
                <Grid container spacing={2}>
                  {checklistItems.map(item => (
                    <Grid item xs={12} sm={6} key={item.itemId}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5, display: 'flex', alignItems: 'center', gap: 1,
                          cursor: canEdit ? 'pointer' : 'default',
                          bgcolor: item.isCompleted ? '#f1f8e9' : 'background.paper',
                          borderColor: item.isCompleted ? '#81c784' : 'divider',
                          transition: 'all 0.15s',
                        }}
                        onClick={() => canEdit && toggleChecklistItem(item.itemId)}
                      >
                        {item.isCompleted
                          ? <CheckCircle sx={{ color: '#388e3c' }} />
                          : <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />
                        }
                        <Typography variant="body2" fontWeight={item.isCompleted ? 600 : 400}>
                          {item.name}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
              {checklist && (
                <>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="NPO Since (Nil by Mouth)"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={npoSince}
                        onChange={(e) => { setNpoSince(e.target.value); setChecklistDirty(true); }}
                        disabled={!canEdit}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Notes"
                        multiline
                        rows={3}
                        value={checklistNotes}
                        onChange={(e) => { setChecklistNotes(e.target.value); setChecklistDirty(true); }}
                        placeholder="Additional pre-operative notes, observations..."
                        disabled={!canEdit}
                      />
                    </Grid>
                  </Grid>
                  {canEdit && (
                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="contained"
                        onClick={saveChecklist}
                        disabled={!checklistDirty || savingChecklist}
                      >
                        {savingChecklist ? 'Saving…' : 'Save Checklist'}
                      </Button>
                      {checklistItems.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {completedItems}/{checklistItems.length} items completed
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </TabPanel>

            {/* ── Tab 3: Post-op Notes ── */}
            <TabPanel value={tab} index={3}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Post-operative Notes" multiline rows={5}
                    value={postOpForm.postOpNotes}
                    onChange={(e) => { setPostOpForm(prev => ({ ...prev, postOpNotes: e.target.value })); setPostOpSaved(false); }}
                    placeholder="Findings during surgery, procedure performed, intraoperative notes..."
                    disabled={!canEdit}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Complications (if any)" multiline rows={3}
                    value={postOpForm.complications}
                    onChange={(e) => { setPostOpForm(prev => ({ ...prev, complications: e.target.value })); setPostOpSaved(false); }}
                    placeholder="Any intraoperative or immediate post-operative complications..."
                    disabled={!canEdit}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Discharge / Follow-up Instructions" multiline rows={4}
                    value={postOpForm.dischargeInstructions}
                    onChange={(e) => { setPostOpForm(prev => ({ ...prev, dischargeInstructions: e.target.value })); setPostOpSaved(false); }}
                    placeholder="Wound care, medications, activity restrictions, follow-up appointment..."
                    disabled={!canEdit}
                  />
                </Grid>
              </Grid>
              {canEdit && (
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button variant="contained" onClick={savePostOp} disabled={savingPostOp}>
                    {savingPostOp ? 'Saving…' : 'Save Post-op Notes'}
                  </Button>
                  {postOpSaved && <Typography variant="body2" color="success.main">Saved successfully.</Typography>}
                </Box>
              )}
            </TabPanel>

          </Box>
        </Paper>
      </Container>

      {/* ── Signed Copies Dialog ── */}
      <Dialog
        open={docsDialog.open}
        onClose={() => setDocsDialog({ open: false, consentId: null, consentType: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Signed Copies — {docsDialog.consentType}
        </DialogTitle>
        <DialogContent>
          {/* Hidden file input */}
          <input
            ref={docsFileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={e => handleDocUpload(e.target.files[0])}
          />

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
              disabled={uploading}
              onClick={() => docsFileInputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : 'Upload Signed Copy'}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Accepted: PDF, JPG, PNG. Upload the scanned / photographed signed consent form.
          </Typography>

          {docsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : docs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <InsertDriveFile sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
              <Typography variant="body2">No signed copies uploaded yet.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>File</strong></TableCell>
                  <TableCell><strong>Size</strong></TableCell>
                  <TableCell><strong>Uploaded</strong></TableCell>
                  <TableCell><strong>By</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {docs.map(doc => (
                  <TableRow key={doc.id} hover>
                    <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={doc.fileName}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <InsertDriveFile fontSize="small" color="action" />
                          <Typography variant="body2" noWrap>{doc.fileName}</Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(doc.fileSizeBytes)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption">
                        {new Date(doc.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{doc.uploadedBy || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="View / Download">
                          <IconButton
                            size="small"
                            color="primary"
                            component="a"
                            href={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}/${doc.fileUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <CloudDownload fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDocDelete(doc.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocsDialog({ open: false, consentId: null, consentType: '' })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Consent Form Dialog */}
      <Dialog open={consentDialog.open} onClose={() => setConsentDialog({ open: false, editing: null })} maxWidth="md" fullWidth>
        <DialogTitle>{consentDialog.editing ? 'Edit Consent Form' : 'Add Consent Form'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Consent Type</InputLabel>
                <Select
                  value={consentForm.consentType}
                  label="Consent Type"
                  onChange={(e) => handleConsentTypeChange(e.target.value)}
                >
                  <MenuItem value="Surgery">Surgery Consent</MenuItem>
                  <MenuItem value="Anesthesia">Anesthesia Consent</MenuItem>
                  <MenuItem value="BloodTransfusion">Blood Transfusion Consent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={consentForm.status}
                  label="Status"
                  onChange={(e) => setConsentForm(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Signed">Signed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Consent Text" multiline rows={8}
                value={consentForm.consentText}
                onChange={(e) => setConsentForm(prev => ({ ...prev, consentText: e.target.value }))}
                helperText="You may edit the default template text if needed."
              />
            </Grid>
            <Grid item xs={12}>
              <Divider><Typography variant="caption">Guardian / Witness Details (if applicable)</Typography></Divider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Guardian Name (if patient is minor / unconscious)"
                value={consentForm.guardianName}
                onChange={(e) => setConsentForm(prev => ({ ...prev, guardianName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Relation to Patient"
                value={consentForm.guardianRelation}
                onChange={(e) => setConsentForm(prev => ({ ...prev, guardianRelation: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Witness Name"
                value={consentForm.witnessName}
                onChange={(e) => setConsentForm(prev => ({ ...prev, witnessName: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentDialog({ open: false, editing: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleConsentSubmit}>
            {consentDialog.editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SurgeryDetailPage;
